import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Loader2,
  X,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { StudentMobileShell } from "../components/student/StudentMobileShell";
import { COURSE_CATEGORIES } from "../lib/course-categories";
import {
  buildCourseScoreHistory,
  courseScoreStatus,
  type CourseScoreHistory,
} from "../lib/course-score-history";
import { getAllScores, type ScoreRow } from "../lib/scores-api";
import { getLearningScore } from "../lib/learning-score";
import { apiRequest } from "../lib/queryClient";
import { naCategoryNamesFor } from "../lib/patient-score-categories";
import type { StudentScenarioResponse } from "../lib/student-course-progress";
import type { ScoreApiResponse } from "../types";
import "./student-course-scores.css";

function dateLabel(value: string) {
  return Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleDateString("ja-JP")
    : "日時不明";
}

function ResultScore({ row }: { row?: ScoreRow }) {
  const value = getLearningScore(row);
  return (
    <strong>
      {value?.score ?? "—"}
      <small>{value ? ` / ${value.max}` : ""}</small>
    </strong>
  );
}

export default function StudentCourseScores() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const selectedId = Number(params.get("course"));
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.closest('main')?.scrollTo({ top: 0 });
  }, [selectedId]);
  const requestedAttempt = Number(params.get("attempt"));
  const category = params.get("category") || "all";
  const filter = params.get("status") || "all";
  const term = params.get("q") || "";
  const scenarioQuery = useQuery<StudentScenarioResponse>({
    queryKey: ["/api/scenarios"],
    staleTime: 30_000,
  });
  const scoreQuery = useQuery({
    queryKey: ["student-mobile", "scores", "all"],
    queryFn: getAllScores,
    staleTime: 60_000,
    refetchOnMount: "always",
  });
  const { courses, unassigned } = useMemo(
    () =>
      buildCourseScoreHistory(
        scenarioQuery.data?.scenarios ?? [],
        scoreQuery.data?.scores ?? [],
      ),
    [scenarioQuery.data, scoreQuery.data],
  );
  const selected = courses.find((c) => c.scenario.id === selectedId);
  const update = (changes: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(search);
    Object.entries(changes).forEach(([key, value]) =>
      value === null ? next.delete(key) : next.set(key, value),
    );
    setLocation(
      `/mypage/scores${next.toString() ? `?${next.toString()}` : ""}`,
      { replace },
    );
  };
  const retry = () => {
    void scenarioQuery.refetch();
    void scoreQuery.refetch();
  };
  const filtered = courses.filter(
    (c) =>
      (category === "all" ||
        String(c.scenario.courseCategoryId) === category) &&
      (filter === "all" ||
        (filter === "passed" ? c.completed : !c.completed)) &&
      `${c.title} ${c.category} ${c.scenario.summary ?? ""}`
        .toLocaleLowerCase()
        .includes(term.trim().toLocaleLowerCase()),
  );
  return (
    <StudentMobileShell
      active="scores"
      eyebrow="LEARNING SCORE"
      title="学習スコア"
    >
      <div className="student-course-scores" ref={contentRef}>
        <div className="score-heading">
          <span>LEARNING SCORE</span>
          <h1>学習スコア</h1>
        </div>
        {scenarioQuery.isLoading || scoreQuery.isLoading ? (
          <p role="status">
            <Loader2 size={18} className="animate-spin" />
            学習記録を読み込み中...
          </p>
        ) : scenarioQuery.isError || scoreQuery.isError ? (
          <div className="score-error" role="alert">
            <p>学習記録を読み込めませんでした。</p>
            <button className="score-action" onClick={retry}>
              <RefreshCw size={16} />
              再読み込み
            </button>
          </div>
        ) : selectedId && !selected ? (
          <>
            <p>この講座は現在公開されていません。</p>
            <button
              className="score-back"
              onClick={() => update({ course: null, attempt: null })}
            >
              <ArrowLeft size={16} />
              講座一覧
            </button>
          </>
        ) : selected ? (
          <CourseDetail
            key={selected.scenario.id}
            course={selected}
            requestedAttempt={requestedAttempt}
            onBack={() => update({ course: null, attempt: null })}
            onAttempt={(id) => update({ attempt: id == null ? null : String(id) }, true)}
            onEvaluation={(id) => setLocation(`/evaluation?interview_id=${id}`)}
            onPractice={() => {
              const description =
                selected.scenario.summary?.trim() ||
                selected.scenario.tasks[0]?.title ||
                selected.title;
              window.sessionStorage.setItem(
                "student-task-briefing",
                JSON.stringify({
                  instructionId: selected.scenario.id,
                  categoryName: selected.category,
                  patientName: "患者",
                  taskTitle: description,
                  taskText: description,
                  questionnaireText: null,
                }),
              );
              setLocation(
                `/mypage/task-briefing?instructionId=${selected.scenario.id}`,
              );
            }}
          />
        ) : (
          <>
            <div className="score-summary">
              <div>
                <span>クリアした講座</span>
                <strong>
                  {courses.filter((c) => c.completed).length}
                  <small> / {courses.length}</small>
                </strong>
              </div>
              <div>
                <span>練習中の講座</span>
                <strong>
                  {
                    courses.filter((c) => c.attempts.length && !c.completed)
                      .length
                  }
                </strong>
              </div>
              <div>
                <span>講座内の練習回数</span>
                <strong>
                  {courses.reduce((n, c) => n + c.attempts.length, 0)}
                  <small> 回</small>
                </strong>
              </div>
            </div>
            <div className="score-toolbar">
              <label className="score-search">
                <Search size={17} />
                <input
                  aria-label="講座を検索"
                  placeholder="講座を検索"
                  value={term}
                  onChange={(e) => update({ q: e.target.value || null }, true)}
                />
              </label>
              <select
                aria-label="カテゴリ"
                value={category}
                onChange={(e) => update({ category: e.target.value }, true)}
              >
                <option value="all">すべてのカテゴリ</option>
                {COURSE_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="score-tabs" role="group" aria-label="講座の状態">
              {[
                ["all", "すべて"],
                ["active", "未クリア"],
                ["passed", "クリア済み"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  aria-pressed={filter === value}
                  onClick={() => update({ status: value }, true)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="score-list-heading">
              <h2>講座ごとの記録</h2>
              <span>{filtered.length} 講座</span>
            </div>
            {!filtered.length && (
              <p className="score-empty">
                {courses.length
                  ? "該当する講座はありません。"
                  : "公開中の講座はありません。"}
              </p>
            )}
            {filtered.map((course) => (
              <button
                className="score-course"
                key={course.scenario.id}
                onClick={() =>
                  update({ course: String(course.scenario.id), attempt: null })
                }
              >
                <div className="score-course-top">
                  <span>
                    {course.category} · 講座 {course.number ?? "—"}
                  </span>
                  <span
                    className={`score-status ${course.completed ? "passed" : ""}`}
                  >
                    {course.completed && <Check size={13} />}{" "}
                    {courseScoreStatus(course)}
                  </span>
                </div>
                <h3>{course.title}</h3>
                <div className="score-course-metrics">
                  <div>
                    <span>最高総合点</span>
                    <strong>
                      {course.best ?? "—"}
                      <small> / 100</small>
                    </strong>
                  </div>
                  <div>
                    <span>最新の結果</span>
                    <ResultScore row={course.latest} />
                  </div>
                  <div>
                    <span>初回→最新の総合点</span>
                    <strong className="score-delta">
                      {course.delta === null
                        ? "—"
                        : `${course.delta >= 0 ? "+" : ""}${course.delta}`}
                      <small>{course.delta === null ? "" : " 点"}</small>
                    </strong>
                  </div>
                </div>
                <div className="score-course-footer">
                  <span>
                    {course.attempts.length}回
                    {course.latest
                      ? ` · 最終 ${dateLabel(course.latest.created)}`
                      : ""}
                  </span>
                  <span>
                    記録を見る
                    <ChevronRight size={16} />
                  </span>
                </div>
              </button>
            ))}
            {!!unassigned.length && (
              <details className="score-section">
                <summary>
                  講座に紐付いていない履歴（{unassigned.length}件）
                </summary>
                {unassigned.map((row) => (
                  <button
                    className="score-attempt"
                    key={row.interview_id}
                    onClick={() =>
                      setLocation(
                        `/evaluation?interview_id=${row.interview_id}`,
                      )
                    }
                  >
                    <span>
                      面接 #{row.interview_id}
                      <small>{dateLabel(row.created)}</small>
                    </span>
                    <ResultScore row={row} />
                    <ChevronRight size={16} />
                  </button>
                ))}
              </details>
            )}
          </>
        )}
      </div>
    </StudentMobileShell>
  );
}

function CourseDetail({
  course,
  requestedAttempt,
  onBack,
  onAttempt,
  onEvaluation,
  onPractice,
}: {
  course: CourseScoreHistory;
  requestedAttempt: number;
  onBack: () => void;
  onAttempt: (id: number | null) => void;
  onEvaluation: (id: number) => void;
  onPractice: () => void;
}) {
  const [mode, setMode] = useState<50 | 100>(course.full.length ? 100 : 50);
  // 押した回だけを、その回のすぐ下に開く（何も押していなければ開かない）
  const openAttempt = course.attempts.find((a) => a.interview_id === requestedAttempt) ?? null;
  const openAttemptId = openAttempt?.interview_id ?? null;
  useEffect(() => {
    if (openAttemptId == null) return;
    document
      .getElementById(`score-attempt-${openAttemptId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [openAttemptId]);
  const points = course.attempts.flatMap((row, index) => {
    if (mode === 100 && !course.full.includes(row)) return [];
    const score = getLearningScore(row);
    return score?.max === mode
      ? [
          {
            attempt: index + 1,
            date: dateLabel(row.created),
            score: score.score,
          },
        ]
      : [];
  });
  return (
    <>
      <button className="score-back" onClick={onBack}>
        <ArrowLeft size={17} />
        講座一覧
      </button>
      <div className="score-detail-heading">
        <span>
          {course.category} · 講座 {course.number ?? "—"}
        </span>
        <h2>{course.title}</h2>
        <span className={`score-status ${course.completed ? "passed" : ""}`}>
          {courseScoreStatus(course)}
        </span>
      </div>
      <div className="score-summary">
        <div>
          <span>最高総合点</span>
          <strong>
            {course.best ?? "—"}
            <small> / 100</small>
          </strong>
        </div>
        <div>
          <span>練習回数</span>
          <strong>
            {course.attempts.length}
            <small> 回</small>
          </strong>
        </div>
        <div>
          <span>クリア基準</span>
          <strong>
            80<small> / 100</small>
          </strong>
        </div>
      </div>
      <section className="score-section">
        <h2>この講座のスコア推移</h2>
        <div className="score-tabs" role="group" aria-label="採点区分">
          {([100, 50] as const).map((max) => (
            <button
              key={max}
              aria-pressed={mode === max}
              onClick={() => setMode(max)}
            >
              {max === 100 ? "面接＋報告 / 100点" : "患者面接のみ / 50点"}
            </button>
          ))}
        </div>
        {points.length ? (
          <div
            className="score-chart"
            role="img"
            aria-label={`${mode}点満点の推移：${points.map((p) => `第${p.attempt}回 ${p.score}点`).join("、")}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={points}
                margin={{ top: 20, right: 20, bottom: 8, left: -20 }}
              >
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis
                  dataKey="attempt"
                  tickFormatter={(n) => `第${n}回`}
                  tick={{ fontSize: 12 }}
                />
                <YAxis domain={[0, mode]} tick={{ fontSize: 12 }} />
                <Tooltip
                  labelFormatter={(v, payload) =>
                    `第${v}回 · ${payload?.[0]?.payload?.date ?? ""}`
                  }
                  formatter={(v: number) => [`${v} / ${mode}点`, "得点"]}
                />
                {mode === 100 && (
                  <ReferenceLine
                    y={80}
                    stroke="#15803D"
                    strokeDasharray="4 4"
                    label={{
                      value: "クリア 80",
                      fontSize: 11,
                      fill: "#15803D",
                      position: "insideTopLeft",
                    }}
                  />
                )}
                <Line
                  type="linear"
                  dataKey="score"
                  stroke="#0E7490"
                  strokeWidth={3}
                  dot={{ r: 5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="score-empty">
            {mode === 100
              ? "報告まで完了した記録はありません。"
              : "患者面接のみの記録はありません。"}
          </p>
        )}
        {mode === 50 && (
          <p className="score-note">
            患者面接のみは途中評価です。講座クリアには報告付きの総合80点以上が必要です。
          </p>
        )}
      </section>
      <section className="score-section">
        <h2>練習履歴</h2>
        {!course.attempts.length && (
          <p className="score-empty">まだ練習記録はありません。</p>
        )}
        {[...course.attempts].reverse().map((row, index) => {
          const isOpen = row.interview_id === openAttemptId;
          const number = course.attempts.length - index;
          return (
          <div key={row.interview_id} id={`score-attempt-${row.interview_id}`} className="score-attempt-group">
          <button
            className="score-attempt"
            aria-expanded={isOpen}
            aria-controls={`score-attempt-detail-${row.interview_id}`}
            onClick={() => onAttempt(isOpen ? null : row.interview_id)}
          >
            <span>
              第{number}回
              <small>
                {dateLabel(row.created)} ·{" "}
                {getLearningScore(row)?.max === 100
                  ? "面接＋報告"
                  : getLearningScore(row)
                    ? "患者面接のみ"
                    : "評価未完了"}
              </small>
            </span>
            <ResultScore row={row} />
            <ChevronRight size={16} className="score-attempt-chevron" />
          </button>
          {isOpen && (
            <AttemptFeedback
              interviewId={row.interview_id}
              number={number}
              onEvaluation={() => onEvaluation(row.interview_id)}
              onClose={() => onAttempt(null)}
            />
          )}
          </div>
          );
        })}
      </section>
      <button className="score-practice" onClick={onPractice}>
        {course.attempts.length ? "この講座をもう一度練習" : "この講座を始める"}
        <ArrowRight size={17} />
      </button>
    </>
  );
}

function AttemptFeedback({
  interviewId,
  number,
  onEvaluation,
  onClose,
}: {
  interviewId: number;
  number: number;
  onEvaluation: () => void;
  onClose: () => void;
}) {
  const query = useQuery<ScoreApiResponse>({
    queryKey: ["course-score-evaluation", interviewId],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/score/by-interview/${interviewId}`,
      );
      const data: ScoreApiResponse = await response.json();
      if (!data.success)
        throw new Error(data.message || "評価を取得できませんでした");
      return data;
    },
    staleTime: 30_000,
  });
  const data = query.data;
  const excludedNames = naCategoryNamesFor(
    data?.patientNaCategories ?? [],
    data?.data?.itemScores,
  );
  const categories = Object.entries(data?.data?.categoryScores ?? {}).filter(
    ([name, score]) => !excludedNames.has(name) && score.maxScore > 0,
  );
  return (
    <section className="score-attempt-detail" id={`score-attempt-detail-${interviewId}`}>
      <div className="score-attempt-detail-heading">
        <h2>第{number}回の評価</h2>
        <button type="button" className="score-close" onClick={onClose} aria-label={`第${number}回の評価を閉じる`}>
          <X size={16} />
          閉じる
        </button>
      </div>
      {query.isLoading ? (
        <p role="status">評価を読み込み中...</p>
      ) : query.isError ? (
        <div role="alert">
          <p>評価を読み込めませんでした。</p>
          <button className="score-action" onClick={() => void query.refetch()}>
            <RefreshCw size={16} />
            再読み込み
          </button>
        </div>
      ) : (
        <>
          <h3>患者面接・項目別の達成率</h3>
          {categories.length ? (
            categories.map(([name, score]) => {
              const percentage = Math.round(
                (score.score / score.maxScore) * 100,
              );
              return (
                <div className="score-item" key={name}>
                  <span>{name}</span>
                  <meter
                    aria-label={name}
                    min={0}
                    max={100}
                    value={percentage}
                  />
                  <strong>{percentage}%</strong>
                </div>
              );
            })
          ) : (
            <p className="score-note">項目別の評価はありません。</p>
          )}
          <h3>{data?.onePointLesson?.title || "ワンポイント"}</h3>
          <p className="score-advice score-feedback">
            {data?.onePointLesson?.message || "ワンポイントはまだありません。"}
          </p>
          <h3>総評</h3>
          <p className="score-feedback">
            {data?.data?.summaryComment ||
              data?.data?.fallbackSummaryComment ||
              (data?.data?.summaryStatus === "pending"
                ? "総評を準備中です。"
                : "総評はまだありません。")}
          </p>
          {data?.dentistData?.summaryComment && (
            <>
              <h3>報告の総評</h3>
              <p className="score-feedback">
                {data.dentistData.summaryComment}
              </p>
            </>
          )}
          {(data?.status === "pending" ||
            data?.status === "running" ||
            data?.data?.summaryStatus === "pending") && (
            <button
              className="score-action"
              onClick={() => void query.refetch()}
            >
              <RefreshCw size={16} />
              評価を更新
            </button>
          )}
        </>
      )}
      <div className="score-attempt-detail-actions">
        <button className="score-action" onClick={onEvaluation}>
          評価結果をすべて見る
          <ChevronRight size={16} />
        </button>
        <button type="button" className="score-action" onClick={onClose}>
          <X size={16} />
          閉じる
        </button>
      </div>
    </section>
  );
}
